/**
 * Contrato da tesouraria (spec 02 §9.2 · tela 6.2 · passo 4.6).
 *
 * **Saldo e disponível são números diferentes**, e a tela mostra os dois: o disponível
 * desconta o bloqueado, porque dinheiro bloqueado está na conta e não paga fornecedor
 * (§16.1 do CONCEITO). Confundi-los faz a tesouraria prometer pagamento que não cumpre.
 */
import { z } from 'zod'
import { chaveDeComando, codigoDeMoeda, dataNominal, uuid } from './comum.js'
import { integracaoDeConta, tipoDeContaBancaria } from './contas-bancarias.js'
import { referenciaDeFicha } from './referencias.js'

export const direcaoDoMovimento = z.enum(['credito', 'debito'])
export type DirecaoDoMovimento = z.infer<typeof direcaoDoMovimento>

export const origemDoMovimento = z.enum(['sicoob_api', 'ofx', 'cnab', 'csv', 'manual'])

export const resultadoDaImportacaoDeMovimentos = z.object({
  /** A conta tocada; também ancora a trilha de auditoria do lote. */
  id: uuid,
  contaBancariaId: uuid,
  formato: z.enum(['ofx', 'cnab']),
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  movimentosLidos: z.number().int().nonnegative(),
  movimentosCriados: z.number().int().nonnegative(),
  duplicadosIgnorados: z.number().int().nonnegative(),
  saldoAnterior: z.string(),
  saldoFinal: z.string(),
})
export type ResultadoDaImportacaoDeMovimentos = z.infer<typeof resultadoDaImportacaoDeMovimentos>

/**
 * O vocabulário inteiro já existe, mas só `novo` é alcançável no 4.6: quem move o
 * estado é a conciliação, que é da fase 6 — e ela é SEMPRE humana (proibição
 * permanente: nada de auto-conciliação, nem com 100% de score).
 */
export const estadoDoMovimento = z.enum([
  'novo', 'sugerido', 'confirmado', 'sem_correspondencia', 'ignorado',
])
export type EstadoDoMovimento = z.infer<typeof estadoDoMovimento>

export const tipoDeCandidatoDaConciliacao = z.enum([
  'recebivel', 'adiantamento', 'conta_pagar', 'comissao', 'rh', 'transferencia', 'avulso',
  // DS-620: a previsão de caixa aberta, realizada por valor (500.000 previstos, 300.000 realizados, restam 200.000).
  'previsao',
])
export type TipoDeCandidatoDaConciliacao = z.infer<typeof tipoDeCandidatoDaConciliacao>

/**
 * O fato bancário autorizado (DS-614, D-175): as únicas categorias que nascem do próprio movimento,
 * com confirmação humana. A conta vem da categoria (tarifa, IOF e encargo na 6.2, rendimento na 6.1),
 * nunca da escolha de quem concilia. O avulso SEM categoria só lança aporte, empréstimo e distribuição.
 */
export const categoriaDoFatoBancario = z.enum(['tarifa_bancaria', 'iof', 'encargo_bancario', 'rendimento'])
export type CategoriaDoFatoBancario = z.infer<typeof categoriaDoFatoBancario>

export const sinalDaConciliacao = z.object({
  codigo: z.enum(['direcao', 'documento', 'nome', 'valor', 'data', 'empate', 'regra', 'deterministico']),
  bateu: z.boolean(),
  texto: z.string(),
})
export type SinalDaConciliacao = z.infer<typeof sinalDaConciliacao>

export const candidatoDaConciliacao = z.object({
  tipo: tipoDeCandidatoDaConciliacao,
  id: uuid,
  referencia: z.string(),
  nome: z.string(),
  documento: z.string().nullable(),
  valor: z.string().nullable(),
  data: dataNominal.nullable(),
  direcao: direcaoDoMovimento.nullable(),
  score: z.number().int().min(0).max(100).nullable(),
  sinais: z.array(sinalDaConciliacao),
  /**
   * O registro com ficha por trás do candidato (D-136, DS-538). O `id` acima é o da PARCELA
   * (recebível, parcela a pagar, parcela de comissão, pagamento de RH), que não tem ficha:
   * a ficha é o pedido, a conta a pagar ou o colaborador. Nula na transferência e no avulso.
   */
  ficha: referenciaDeFicha.nullable(),
  /** A parte que paga ou recebe: a empresa do cliente, o fornecedor, o parceiro, a PJ ou o colaborador. */
  favorecido: referenciaDeFicha.nullable(),
  /** Só no avulso de fato bancário (DS-614); o `id` dele é a identidade da categoria, não da conta. */
  categoria: categoriaDoFatoBancario.nullable(),
})
export type CandidatoDaConciliacao = z.infer<typeof candidatoDaConciliacao>

export const alvoConfirmadoDaConciliacao = z.object({
  tipo: z.enum([
    'liquidacao_recebivel', 'adiantamento_cliente', 'liquidacao_pagar',
    'liquidacao_comissao', 'liquidacao_rh', 'transferencia', 'avulso', 'previsao_caixa',
  ]),
  id: uuid.nullable(),
  valor: z.string(),
  contaContabilId: uuid.nullable(),
  referencia: z.string(),
  /** A ficha do registro liquidado: o pedido, a conta a pagar ou o colaborador (D-136, DS-538). */
  ficha: referenciaDeFicha.nullable(),
  /** A categoria do fato bancário, quando o avulso é um (DS-614). */
  categoria: categoriaDoFatoBancario.nullable(),
})
export type AlvoConfirmadoDaConciliacao = z.infer<typeof alvoConfirmadoDaConciliacao>

const valorPositivoDaConciliacao = z.string().regex(
  /^(?:0\.(?:0[1-9]|[1-9]\d)|[1-9]\d{0,15}\.\d{2})$/,
  'Use um valor positivo com exatamente duas casas decimais.',
)

export const itemDaBandejaDeConciliacao = z.object({
  id: uuid,
  contaBancariaId: uuid,
  contaNome: z.string(),
  data: dataNominal,
  dataLote: dataNominal.nullable(),
  valor: z.string(),
  direcao: direcaoDoMovimento,
  historico: z.string().nullable(),
  documento: z.string().nullable(),
  contraparteNome: z.string().nullable(),
  contraparteDocumento: z.string().nullable(),
  origem: origemDoMovimento,
  estado: estadoDoMovimento,
  motivoIgnorado: z.string().nullable(),
  motivoIgnoradoObs: z.string().nullable(),
  conciliacaoId: uuid.nullable(),
  /** Par de efeito líquido zero; continua exigindo que uma pessoa confirme o motivo. */
  recomendacaoIgnorar: z.object({
    parId: uuid,
    motivoId: uuid,
    texto: z.string(),
  }).nullable(),
  sugestao: candidatoDaConciliacao.nullable(),
  outrosCandidatos: z.array(candidatoDaConciliacao),
  alvosConfirmados: z.array(alvoConfirmadoDaConciliacao),
})
export type ItemDaBandejaDeConciliacao = z.infer<typeof itemDaBandejaDeConciliacao>

export const entradaDeAlvoDaConciliacao = z.object({
  tipo: tipoDeCandidatoDaConciliacao,
  id: uuid.optional(),
  valor: valorPositivoDaConciliacao,
  contaContabilId: uuid.optional(),
  /**
   * DS-614: o fato bancário. Com a categoria, a conta vem dela (o `contaContabilId` é ignorado), a
   * competência é o mês da DRE (primeiro dia; ausente = o mês do movimento), e o centro de custo e o
   * motivo são opcionais. O motivo também vale para o avulso de movimentação.
   */
  categoria: categoriaDoFatoBancario.optional(),
  competencia: dataNominal.refine((d) => d.endsWith('-01'), 'A competência é o primeiro dia do mês.').optional(),
  centroCustoId: uuid.optional(),
  motivo: z.string().trim().min(1).max(500).optional(),
}).superRefine((alvo, contexto) => {
  if (alvo.tipo === 'avulso') return
  for (const campo of ['categoria', 'competencia', 'centroCustoId', 'motivo'] as const) {
    if (alvo[campo] !== undefined) contexto.addIssue({ code: 'custom', path: [campo], message: 'Só o avulso aceita este campo.' })
  }
})

export const entradaDeConciliacao = z.object({
  alvos: z.array(entradaDeAlvoDaConciliacao).min(1),
  /** A pessoa escolhe ensinar; confirmar por si só nunca cria memória silenciosa. */
  aprenderRegra: z.boolean().optional(),
})
export type EntradaDeConciliacao = z.infer<typeof entradaDeConciliacao>

/**
 * O aviso antes do avulso num débito (DS-614): a conta a pagar parecida que pode ser a mesma despesa. A
 * parcela aberta em qualquer estado (o motor só enxerga a aprovada) e a baixa manual sem movimento.
 * Avisa; não impede.
 */
export const contaAPagarSemelhante = z.object({
  tipo: z.enum(['parcela_aberta', 'baixa_sem_movimento']),
  contaPagarId: uuid,
  referencia: z.string(),
  credor: z.string(),
  valor: z.string(),
  data: dataNominal,
  status: z.string(),
})
export type ContaAPagarSemelhante = z.infer<typeof contaAPagarSemelhante>

export const entradaParaIgnorarMovimento = z.object({
  motivoId: uuid,
  observacao: z.string().trim().min(1).optional(),
})
export type EntradaParaIgnorarMovimento = z.infer<typeof entradaParaIgnorarMovimento>

export const entradaDeRegraDaConciliacao = z.object({
  documentoContraparte: z.string().trim().min(1).optional(),
  padraoHistorico: z.string().trim().min(1).optional(),
  destinoSugerido: z.object({
    tipo: tipoDeCandidatoDaConciliacao,
    id: uuid.optional(),
    contaContabilId: uuid.optional(),
    /** DS-614: a regra que aprende um fato bancário guarda a categoria, não a conta. */
    categoria: categoriaDoFatoBancario.optional(),
    referencia: z.string().min(1),
  }),
  criadaDe: uuid.optional(),
}).refine(
  (e) => Number(e.documentoContraparte !== undefined) + Number(e.padraoHistorico !== undefined) === 1,
  { message: 'Informe exatamente uma condição: documento ou padrão de histórico.' },
)
export type EntradaDeRegraDaConciliacao = z.infer<typeof entradaDeRegraDaConciliacao>

export const regraDaConciliacao = z.object({
  id: uuid,
  documentoContraparte: z.string().nullable(),
  padraoHistorico: z.string().nullable(),
  destinoSugerido: z.object({
    tipo: tipoDeCandidatoDaConciliacao,
    id: uuid.optional(),
    contaContabilId: uuid.optional(),
    categoria: categoriaDoFatoBancario.optional(),
    referencia: z.string(),
    /** A ficha do registro do destino, lida na hora (D-136, DS-538); nula na classificação avulsa. */
    ficha: referenciaDeFicha.nullable(),
  }),
  criadaDe: uuid.nullable(),
  ativa: z.boolean(),
})
export type RegraDaConciliacao = z.infer<typeof regraDaConciliacao>

export const movimentoBancario = z.object({
  id: uuid,
  contaBancariaId: uuid,
  data: dataNominal,
  dataLote: dataNominal.nullable(),
  valor: z.string(),
  direcao: direcaoDoMovimento,
  historico: z.string().nullable(),
  documento: z.string().nullable(),
  contraparteNome: z.string().nullable(),
  /** CNPJ/CPF da contraparte, separado do nome na importação (migration 0036). */
  contraparteDocumento: z.string().nullable(),
  origem: origemDoMovimento,
  estado: estadoDoMovimento,
  /**
   * Movimento entre contas PRÓPRIAS — não é entrada nem saída de caixa.
   *
   * A varredura do RDC responde por um terço das saídas do extrato da conta corrente, e
   * mostrá-la como saída faria a média de queima parecer o dobro do que é. A tela marca
   * essas linhas para que a soma do extrato e a curva de caixa não pareçam discordar.
   */
  transferencia: z.boolean(),
})
export type MovimentoBancario = z.infer<typeof movimentoBancario>

/**
 * Crédito confirmado pela API Pix, ainda sem vínculo inequívoco com o extrato.
 * É visível, mas não compõe saldo nem curva até o banco publicar a segunda fonte.
 */
export const pixRecebidoPendente = z.object({
  id: uuid,
  contaBancariaId: uuid,
  data: dataNominal,
  recebidoEm: z.string(),
  valor: z.string(),
  pagadorNome: z.string().nullable(),
  pagadorDocumento: z.string().nullable(),
  txid: z.string().nullable(),
})
export type PixRecebidoPendente = z.infer<typeof pixRecebidoPendente>

/**
 * Uma linha da lista de contas da 6.2 — o cadastro e a posição na MESMA linha.
 *
 * O cadastro vem junto de propósito. A tela mostra nove colunas (Conta · Tipo · Moeda ·
 * Integração · Disponível · Bloqueado · Limite · Saldo total · Ações); buscar metade
 * numa rota e metade noutra obrigaria o cliente a casar duas listas, que é a segunda
 * fonte de verdade de sempre, disfarçada de join no front.
 *
 * **`saldo` = `disponivel` + `bloqueado`, por construção** — a view define o disponível
 * como saldo menos bloqueado. A tela mostra os três e não soma nenhum.
 */
export const saldoDaConta = z.object({
  contaBancariaId: uuid,
  nome: z.string(),
  tipo: tipoDeContaBancaria,
  banco: z.string().nullable(),
  agencia: z.string().nullable(),
  numero: z.string().nullable(),
  integracao: integracaoDeConta,
  moeda: codigoDeMoeda,
  saldoInicial: z.string(),
  saldoInicialEm: dataNominal,
  /** Disponível + bloqueado. É o "Saldo total" da coluna da tela. */
  saldo: z.string(),
  /** Saldo − bloqueado. É este que a tesouraria usa para decidir pagamento. */
  disponivel: z.string(),
  bloqueado: z.string(),
  /** Limite contratado. NÃO entra no saldo total — é crédito, não dinheiro. */
  limite: z.string().nullable(),
  movimentos: z.number().int(),
})
export type SaldoDaConta = z.infer<typeof saldoDaConta>

/**
 * Um ponto da curva de saldo consolidado (G20).
 *
 * `saldo` é ACUMULADO (quanto a empresa tinha ao fim daquele mês); `entradas` e
 * `saidas` são o fluxo DO mês e **excluem transferência entre contas próprias** — sem
 * isso a varredura automática do RDC apareceria como caixa entrando e saindo, e ela
 * sozinha responde por 22% dos lançamentos da conta corrente.
 */
export const pontoDaCurva = z.object({
  mes: dataNominal,
  saldo: z.string(),
  entradas: z.string(),
  saidas: z.string(),
  cobertura: z.enum(['verificada', 'parcial', 'desconhecida']),
})
export type PontoDaCurva = z.infer<typeof pontoDaCurva>

/**
 * O resumo HISTÓRICO do caixa, que acompanha a curva realizada (`GET /caixa/curva` sem
 * período). O nome ficou por compatibilidade; o horizonte de verdade é o do resumo da
 * curva projetada (`horizonteProjetadoDeCaixa`, DS-580).
 *
 * - `disponivel`: o disponível das contas ATIVAS em reais, lido pela mesma função que dá o
 *   ponto de partida da projeção (DS-582). Antes somava também as contas inativas, e o
 *   rodapé do cartão discordava da curva que ele acompanha.
 * - `burnMensal`: a tela o mostra como **Saídas médias mensais** (DS-583): a média das
 *   saídas BRUTAS dos meses fechados e cobertos, sem transferência entre contas próprias.
 *   Não é consumo líquido e não entra no horizonte. O nome do campo é o de antes, porque
 *   contrato só cresce.
 * - `mesesNaMedia`: o divisor dessa média (de 0 a 6), que a tela escreve no complemento.
 * - `meses`: **LEGADO.** Disponível ÷ saídas médias, a conta que o protótipo fazia e que a
 *   spec proíbe como horizonte. Nenhuma tela o exibe; ele fica no payload só para não
 *   quebrar quem ainda o lê. Nulo quando não há mês fechado com saída.
 */
export const runwayDeCaixa = z.object({
  disponivel: z.string(),
  /** Média mensal das SAÍDAS brutas dos meses fechados e cobertos (até 6). */
  burnMensal: z.string(),
  mesesNaMedia: z.number().int().nonnegative(),
  mesesDeHistorico: z.number().int(),
  /** Legado (ver acima): nenhuma tela exibe. */
  meses: z.number().nullable(),
})
export type RunwayDeCaixa = z.infer<typeof runwayDeCaixa>

/** As camadas que toda grandeza projetada precisa declarar (spec 05 §5); o Programado é a previsão de caixa (DS-620). */
export const camadaDeCaixa = z.enum(['contratado', 'derivado', 'programado', 'orcado'])
export type CamadaDeCaixa = z.infer<typeof camadaDeCaixa>

/**
 * De onde a linha da agenda VEIO.
 *
 * `orcamento` entrou em 03/09 com a camada Orçada (DS-432). Ele é diferente dos outros
 * cinco em espécie: não aponta para um documento que alguém assinou, e sim para um mês
 * de uma versão aprovada. Por isso a linha orçada não abre FICHA nenhuma — não existe
 * registro de "linha orçada de um mês" para abrir.
 *
 * O que ela tem é DONO, e o `origemId` é o dele: a `orcamento_versao` de onde ela saiu.
 * A tela leva à grade daquela versão (`8.2?v=&scope=`, spec 6.1 §Deep-links), e não a
 * uma ficha que não existe. A distinção importa porque a primeira leitura desta nota já
 * a entendeu como "não linka", e o que estava escrito aqui autorizava as duas leituras.
 */
export const origemDaAgendaDeCaixa = z.enum([
  'pedido', 'recebivel', 'conta_pagar', 'comissao', 'rh', 'orcamento',
  // 'campanha' saiu na v1.9.0 (D-193 (9), DS-686 (j)): Campanhas não projeta caixa, e o custo de
  // mídia projetado que a usava (DS-537) deixou a agenda.
  // O tributo reconhecido e ainda sem guia, na camada Derivado (DS-615). O origemId é a regra do
  // calendário fiscal que dá a data; a linha não tem ficha.
  'tributo',
  // A previsão de caixa, na camada Programado (DS-620). O origemId é a previsão; a linha não tem ficha.
  'previsao_caixa',
])
export type OrigemDaAgendaDeCaixa = z.infer<typeof origemDaAgendaDeCaixa>

/**
 * O tributo que o caixa NÃO data (DS-615, pedido de 01/10 §4): reconhecido (camada Derivado) ou orçado
 * (Orçado, Fase 1c) sem regra no calendário fiscal. Fica fora da curva datada, com o valor à vista.
 */
export const pendenciaFiscalDoCaixa = z.object({
  /** Nulo no orçado do 7.x sem regra: sem o calendário, a conta não diz se é IRPJ ou CSLL (DS-617). */
  tributo: z.enum(['ISS', 'PIS', 'COFINS', 'CBS', 'IBS', 'IRPJ', 'CSLL']).nullable(),
  /** A conta ("7.1 · IRPJ"), quando o tributo não se sabe. */
  conta: z.string().nullable(),
  competencia: dataNominal,
  valor: z.string(),
  camada: z.enum(['derivado', 'orcado']),
})
export type PendenciaFiscalDoCaixa = z.infer<typeof pendenciaFiscalDoCaixa>

const valorPositivoDaPrevisao = z.string().regex(
  /^(?:0\.(?:0[1-9]|[1-9]\d)|[1-9]\d{0,15}(?:\.\d{1,2})?)$/,
  'Use um valor positivo com até duas casas decimais.',
)

/**
 * DS-620 (D-176): a Previsão de caixa. O evento futuro sem documento e sem DRE — 8.1 Aporte, 8.2 Captação de
 * empréstimo, 8.3 Distribuição de lucros —, na camada Programado do fluxo até o dinheiro chegar. 8.4 e 8.5 ficam
 * fora: não mudam o caixa consolidado.
 */
export const eventoDaPrevisao = z.enum(['aporte', 'emprestimo', 'distribuicao_lucros'])
export type EventoDaPrevisao = z.infer<typeof eventoDaPrevisao>

/** O estado estrutural (com motivo quando deixa de ser ativa) e a leitura derivada do realizado e da data. */
export const estadoDaPrevisao = z.enum(['ativa', 'encerrada', 'cancelada'])
export const situacaoDaPrevisao = z.enum(['prevista', 'parcial', 'vencida', 'realizada', 'encerrada', 'cancelada'])
export type SituacaoDaPrevisao = z.infer<typeof situacaoDaPrevisao>

export const previsaoDeCaixa = z.object({
  id: uuid,
  evento: eventoDaPrevisao,
  contaContabilId: uuid,
  contaNome: z.string(),
  direcao: direcaoDoMovimento,
  descricao: z.string(),
  valor: z.string(),
  /** O que a conciliação já realizou (500.000 previstos, 300.000 realizados). */
  realizado: z.string(),
  /** O que ainda se espera (200.000); zero na encerrada e na cancelada. */
  restante: z.string(),
  dataPrevista: dataNominal,
  contaBancariaId: uuid.nullable(),
  contaBancariaNome: z.string().nullable(),
  estado: estadoDaPrevisao,
  situacao: situacaoDaPrevisao,
  motivoEstado: z.string().nullable(),
  criadaEm: dataNominal,
})
export type PrevisaoDeCaixa = z.infer<typeof previsaoDeCaixa>

export const entradaDePrevisaoDeCaixa = z.object({
  evento: eventoDaPrevisao,
  descricao: z.string().trim().min(3, 'Descreva a previsão.'),
  valor: valorPositivoDaPrevisao,
  dataPrevista: dataNominal,
  contaBancariaId: uuid.nullable().optional(),
})
export type EntradaDePrevisaoDeCaixa = z.infer<typeof entradaDePrevisaoDeCaixa>

/** Remarcar a data, corrigir a descrição ou o valor (nunca abaixo do já realizado). */
export const entradaDeAlteracaoDaPrevisao = z.object({
  dataPrevista: dataNominal.optional(),
  descricao: z.string().trim().min(3).optional(),
  valor: valorPositivoDaPrevisao.optional(),
}).refine((e) => e.dataPrevista !== undefined || e.descricao !== undefined || e.valor !== undefined, {
  message: 'Informe o que muda na previsão.',
})
export type EntradaDeAlteracaoDaPrevisao = z.infer<typeof entradaDeAlteracaoDaPrevisao>

/** Encerrar (o restante deixou de ser esperado) ou cancelar (não aconteceu): sempre com motivo. */
export const entradaDeFinalizacaoDaPrevisao = z.object({
  motivo: z.string().trim().min(3, 'Diga o motivo.'),
})

/**
 * A previsão VENCIDA com restante (D-176): a entrada sai da curva e fica só aqui; a saída cai em hoje na curva e
 * também fica aqui. Remarcar, cancelar ou realizar pela conciliação.
 */
export const previsaoVencidaDoCaixa = z.object({
  id: uuid,
  evento: eventoDaPrevisao,
  descricao: z.string(),
  direcao: direcaoDoMovimento,
  dataPrevista: dataNominal,
  restante: z.string(),
  /** A saída vencida continua na curva (em hoje); a entrada vencida, não. */
  naCurva: z.boolean(),
})
export type PrevisaoVencidaDoCaixa = z.infer<typeof previsaoVencidaDoCaixa>

/**
 * Uma linha da agenda parcial do passo 6.3.
 *
 * `saldoProjetado` é global: conta todas as linhas, mesmo quando a tela oculta uma
 * camada. A conta é nullable porque hoje recebível/comissão não gravam conta-alvo;
 * escolher a primeira conta ativa seria inventar uma decisão financeira.
 */
export const linhaDaAgendaDeCaixa = z.object({
  id: uuid,
  vencimento: dataNominal,
  descricao: z.string(),
  camada: camadaDeCaixa,
  origemTipo: origemDaAgendaDeCaixa,
  origemId: uuid,
  origemCodigo: z.string(),
  contaBancariaId: uuid.nullable(),
  contaNome: z.string().nullable(),
  direcao: direcaoDoMovimento,
  /** DS496: origem legada não conferida; NULL não representa zero. */
  valor: z.string().nullable(),
  /** Desconhecido a partir da primeira pendência, mesmo em linhas de valor conhecido. */
  saldoProjetado: z.string().nullable(),
  naoOperacional: z.boolean(),
  categoriaNaoOperacional: z.string().nullable(),
  /**
   * O registro que o código da linha abre (D-136, DS-538): o pedido (recebível, derivado e
   * comissão), a conta a pagar, o colaborador (RH) ou a versão aprovada (orçado). O `origemId` NÃO
   * muda de significado; a `ficha` é o campo novo.
   */
  ficha: referenciaDeFicha.nullable(),
  /** DS-622: só na linha orçada — a aba da 8.2 onde ela está (receita, gastos ou o resultado financeiro). */
  escopoDoOrcamento: z.enum(['receita', 'gastos', 'financeiro']).nullable().optional(),
})
export type LinhaDaAgendaDeCaixa = z.infer<typeof linhaDaAgendaDeCaixa>

export const agregadosDaAgendaDeCaixa = z.object({
  entradas: z.string().nullable(),
  saidas: z.string().nullable(),
  naoOperacional: z.string().nullable(),
  linhas: z.number().int().nonnegative(),
})
export type AgregadosDaAgendaDeCaixa = z.infer<typeof agregadosDaAgendaDeCaixa>

export const linhaDoCaixaExecutado = z.object({
  id: uuid,
  data: dataNominal,
  descricao: z.string(),
  contaBancariaId: uuid,
  contaNome: z.string(),
  direcao: direcaoDoMovimento,
  valor: z.string(),
  conciliacao: z.enum(['pendente', 'conciliado']),
  origemTipo: z.enum(['recebivel', 'conta_pagar', 'comissao', 'rh', 'movimento']),
  origemId: uuid,
  origemCodigo: z.string().nullable(),
  naoOperacional: z.boolean(),
  categoriaNaoOperacional: z.string().nullable(),
  /**
   * O registro que a descrição abre (D-136, DS-538). Aqui o `origemId` do recebível é a
   * PARCELA, e não o pedido como na agenda: a ficha é o pedido, o colaborador ou a conta a
   * pagar. Nula no movimento solto.
   */
  ficha: referenciaDeFicha.nullable(),
})
export type LinhaDoCaixaExecutado = z.infer<typeof linhaDoCaixaExecutado>

/** Ponto diário da curva prospectiva. */
export const pontoDaCurvaProjetada = z.object({
  data: dataNominal,
  saldo: z.string(),
  entradas: z.string(),
  saidas: z.string(),
})
export type PontoDaCurvaProjetada = z.infer<typeof pontoDaCurvaProjetada>

/** Por que o horizonte ainda não tem resposta. */
export const motivoDoHorizonteNaoEstimavel = z.enum(['sem_saidas_futuras', 'em_conferencia'])
export type MotivoDoHorizonteNaoEstimavel = z.infer<typeof motivoDoHorizonteNaoEstimavel>

/**
 * O HORIZONTE PROJETADO DE CAIXA (DS-580) — o que se chamava runway.
 *
 * Responde uma pergunta só: "com o saldo disponível de hoje e com os movimentos futuros
 * cadastrados, em que data o saldo projetado pode chegar a zero?". NÃO responde por quanto
 * tempo a empresa sobreviveria no ritmo do passado, e nada aqui divide saldo por média.
 *
 * - `estimavel = false` com `motivo = 'sem_saidas_futuras'`: não há saída futura nenhuma no
 *   horizonte (e o saldo de hoje é positivo). A curva só sobe ou fica parada, e "não chega a
 *   zero" seria conclusão tirada da falta de dado.
 * - `estimavel = false` com `motivo = 'em_conferencia'`: há valor em conferência (DS496).
 * - `estimavel = true` e `cruzaEm = null`: há saídas, e o saldo projetado não chega a zero
 *   dentro de `horizonteDias`.
 * - `dias = 0`: o saldo projetado já está em zero ou abaixo hoje.
 *
 * Não depende do período pedido: a 6.2, a 6.1 e o Painel recebem o mesmo objeto. A tela não
 * recalcula nenhum destes campos.
 */
export const horizonteProjetadoDeCaixa = z.object({
  estimavel: z.boolean(),
  motivo: motivoDoHorizonteNaoEstimavel.nullable(),
  horizonteDias: z.number().int().positive(),
  cruzaEm: dataNominal.nullable(),
  dias: z.number().int().nonnegative().nullable(),
  meses: z.number().nonnegative().nullable(),
  /** Movimentos que a agenda ainda espera até o fim do horizonte, vencidos inclusive. */
  movimentosFuturos: z.number().int().nonnegative(),
  saidasFuturas: z.number().int().nonnegative(),
  entradasFuturas: z.number().int().nonnegative(),
  /** As camadas presentes nesses movimentos, na ordem Contratado · Derivado · Orçado. */
  camadas: z.array(camadaDeCaixa),
})
export type HorizonteProjetadoDeCaixa = z.infer<typeof horizonteProjetadoDeCaixa>

export const resumoDaCurvaProjetada = z.object({
  /** O disponível das contas ativas em reais: o ponto de partida da curva (DS-582). */
  saldoHoje: z.string(),
  /** Pendência no horizonte impede curva/horizonte; saldo atual continua conhecido. */
  requerConferencia: z.boolean().optional(),
  /** Menor saldo DENTRO do período pedido (`de`/`ate`), e o dia em que ele acontece. */
  menorSaldo: z.string().nullable(),
  menorSaldoEm: dataNominal.nullable(),
  /**
   * COMPATIBILIDADE: o mesmo cruzamento do `horizonte`, com o nome antigo. Nulo tanto quando
   * o saldo não chega a zero quanto quando o horizonte não é estimável; quem precisa
   * distinguir os dois lê `horizonte`.
   */
  runway: z.object({
    meses: z.number().nullable(),
    cruzaEm: dataNominal.nullable(),
  }),
  horizonte: horizonteProjetadoDeCaixa,
})
export type ResumoDaCurvaProjetada = z.infer<typeof resumoDaCurvaProjetada>

/**
 * Um lançamento à mão (o Caixa não tem extrato para importar).
 *
 * `valor` é SEMPRE positivo — o sinal vive em `direcao`. Aceitar negativo criaria duas
 * maneiras de dizer a mesma coisa, e um crédito de −50 somaria como crédito.
 */
export const entradaDeMovimento = z.object({
  contaBancariaId: uuid,
  data: dataNominal,
  valor: z.string(),
  direcao: direcaoDoMovimento,
  historico: z.string().min(1),
  documento: z.string().nullable().optional(),
  contraparteNome: z.string().nullable().optional(),
})
export type EntradaDeMovimento = z.infer<typeof entradaDeMovimento>

export const tipoDeTransferencia = z.enum(['entre_contas', 'intercompany', 'aplicacao', 'resgate'])
export type TipoDeTransferencia = z.infer<typeof tipoDeTransferencia>

/** Transferência entre contas próprias: dois movimentos, um ato reversível. */
export const entradaDeTransferencia = z.object({
  origem: uuid,
  destino: uuid,
  data: dataNominal,
  valor: z.string().regex(/^(?:0\.(?:0[1-9]|[1-9]\d)|[1-9]\d{0,15}\.\d{2})$/),
  /** Se vier, o servidor confere contra a classificação derivada das contas. */
  tipo: tipoDeTransferencia.optional(),
  observacao: z.string().trim().min(1).optional(),
  /** Idempotência: a mesma intenção reapresentada devolve a mesma transferência. */
  chaveDeComando: chaveDeComando.optional(),
})
export type EntradaDeTransferencia = z.infer<typeof entradaDeTransferencia>

export const transferenciaRegistrada = z.object({
  id: uuid,
  debitoId: uuid,
  creditoId: uuid,
  tipo: tipoDeTransferencia,
})
export type TransferenciaRegistrada = z.infer<typeof transferenciaRegistrada>

/**
 * O estado da integração bancária — medido, não declarado.
 *
 * A tela dizia "Não conectada" em letra fixa e passou a mentir no dia em que a leitura
 * entrou. Cada campo aqui é lido de onde a verdade mora: a credencial do ambiente, as
 * contas do cadastro, o último sync do próprio movimento, e a validade do CERTIFICADO
 * do arquivo — nunca de uma data escrita à mão, que continuaria dizendo a data velha
 * depois da renovação.
 */
export const estadoDaIntegracao = z.object({
  conectada: z.boolean(),
  credencial: z.boolean(),
  ativada: z.boolean(),
  contasIntegradas: z.number().int(),
  ultimoSync: z.string().nullable(),
  ultimaTentativa: z.string().nullable(),
  ultimaExecucaoEstado: z.enum(['iniciada', 'sucesso', 'parcial', 'falha', 'quarentena']).nullable(),
  movimentosImportados: z.number().int(),
  novosNaUltimaLeitura: z.number().int().nonnegative(),
  historicoDe: dataNominal.nullable(),
  historicoAte: dataNominal.nullable(),
  pixPendentes: z.number().int(),
  pixPendentesTotal: z.string(),
  posicaoContaCorrente: z.object({
    capturadaEm: z.string(),
    saldoAtual: z.string(),
    saldoBloqueado: z.string(),
    saldoLimite: z.string(),
    saldoBloqueioJudicial: z.string().nullable(),
  }).nullable(),
  posicaoInvestimentos: z.object({
    dataPosicao: dataNominal,
    valorBruto: z.string(),
    valorLiquido: z.string().nullable(),
    liquidezComprovada: z.boolean(),
    aplicacoes: z.number().int().nonnegative(),
    linhasHistoricas: z.number().int().nonnegative(),
    evidenciasEmQuarentena: z.number().int().nonnegative(),
  }).nullable(),
  certificadoAte: dataNominal.nullable(),
  certificadoDiasRestantes: z.number().int().nullable(),
  /*
    Observabilidade da leitura (DS-585). Os campos abaixo são ADITIVOS e opcionais: um
    cliente que ainda não os conhece continua funcionando, e a tela trata a ausência como
    "sem informação", nunca como zero.
  */
  /** A conta cujo extrato o banco entrega: só ela pode ser sincronizada. */
  contaDoExtratoId: uuid.nullable().optional(),
  /** O resultado da última tentativa, em código e frase prontos para a tela. */
  ultimaTentativaResultado: z.object({
    codigo: z.enum(['sucesso', 'parcial', 'em_andamento', 'divergencia', 'falha']),
    mensagem: z.string(),
  }).nullable().optional(),
  /** Há uma leitura desta entidade rodando agora. */
  emAndamento: z.boolean().optional(),
  /** Controles de ativação lidos dos Parâmetros. */
  automacao: z.object({ diaria: z.boolean(), intradiaria: z.boolean() }).optional(),
  /** Previsão da próxima leitura automática, no relógio de São Paulo (`AAAA-MM-DDTHH:MM`). */
  proximaLeitura: z.string().nullable().optional(),
  /** A automação parou por falha que não passa sozinha (certificado ou credencial). */
  suspensa: z.object({ motivo: z.string() }).nullable().optional(),
  /** Divergências bancárias abertas, esperando uma pessoa decidir. */
  divergenciasAbertas: z.number().int().nonnegative().optional(),
  /** DS-621: linhas do extrato de investimento que o banco mudou; não param a leitura, mas esperam decisão. */
  divergenciasDeInvestimento: z.number().int().nonnegative().optional(),
})
export type EstadoDaIntegracao = z.infer<typeof estadoDaIntegracao>
